import { uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  BearingEnvelope,
  Building,
  CeilingTray,
  FloorPolyline3D,
  LayoutModel,
  Level,
  TrussEnvelope,
  WorkLineScope,
} from '@paragon/contract-library';
import { getInsertionsWithPendingChanges, getLayoutModels } from '@paragon/layout-models';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { pickSingleActiveLevelGuid } from '../../library/hierarchy-filtering/active-level';
import { VisibilityConfig } from '../../library/hierarchy-filtering/visible-objects';
import { SelectableEntryOfType, SelectableItemEntry, SelectableItemOfType } from '../../library/selection/types';
import { RootState } from '../index';
import { LayoutState } from '../slices/layoutSlice';
import { createGuidSet } from './util/createGuidSet';
import { createSimpleSetSelector } from './util/createSimpleSetSelector';
import { toDictionary } from './util/toDictionary';

type IModelInLevel = { guid: string; levelGuid: string | null };
function filterVisibleItemsInLevels<T extends IModelInLevel>(array: T[], visibleLevels: Set<string | null>): T[] {
  return array.filter((item) => visibleLevels.has(item.levelGuid ?? null));
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// selectors for layout hierarchy stuff
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getAllBuildings = createSelector(
  getLayoutModels,
  (models) => models.filter((model) => model.type === 'building') as (Building & LayoutModel)[],
);
export const getAllBuildingGuids = createSimpleSetSelector(
  getAllBuildings,
  (buildings) => new Set(buildings.map((building) => building.guid)),
);
export const getAllBuildingsDictionary = createSelector(getAllBuildings, toDictionary);

export const getAllLevels = createSelector(
  getLayoutModels,
  (models) => models.filter((model) => model.type === 'level') as (Level & LayoutModel)[],
);
export const getAllLevelGuids = createSimpleSetSelector(
  getAllLevels,
  (levels) => new Set(levels.map((level) => level.guid)),
);
export const getAllLevelsDictionary = createSelector(getAllLevels, toDictionary);

export const getIsLayoutHierarchyPresentInProject = createSelector(
  getAllBuildings,
  getAllLevels,
  (buildings, levels) => buildings.length > 0 || levels.length > 0,
);

export const getLayoutHierarchyFilter = (state: RootState) => state.selections.layoutHierarchyFilter;

/**
 * The GUID of the visible building. Returns `null` if there is not a single visible building.
 */
export const getVisibleBuildingGuidOrNull = createSelector(getLayoutHierarchyFilter, (filter) =>
  filter && filter !== 'ghost' ? filter.activeBuildingGuid : null,
);

/**
 * The visible building. Returns `null` if there is not a single visible building.
 */
export const getVisibleBuildingOrNull = createSelector(
  getAllBuildingsDictionary,
  getVisibleBuildingGuidOrNull,
  (buildings, activeBuildingGuid) => (activeBuildingGuid ? buildings[activeBuildingGuid] : null),
);

/**
 * DO NOT EXPORT. For internal use in this file. See `getVisibleLevelGuids` instead.
 *
 * Determines which layers are "currently visible" entirely based on the user's layout and their active hierarchy
 *   filter, without taking into account any special cases which might also affect visibility.
 *
 * The inclusion of `null` in the set signifies that items without a level guid should also be visible.
 */
const getOstensiblyVisibleLevelGuids = createSimpleSetSelector(
  getAllLevelGuids,
  getInsertionsWithPendingChanges,
  getLayoutHierarchyFilter,

  (allLevelGuids, insertions, hierarchyFilter): Set<string | null> => {
    // in "ghost objects" mode we are only showing objects that don't belong to any building so they can be fixed
    if (hierarchyFilter === 'ghost') {
      return new Set([null]);
    }

    let visibleLevelGuids = Array.from(allLevelGuids); // start with all levels and filter them down

    // if there is no hierarchy filter, all levels (and any objects not belonging to a level) are visible
    if (hierarchyFilter == null) {
      return new Set([...allLevelGuids, null]);
    }

    if (hierarchyFilter?.activeBuildingGuid) {
      visibleLevelGuids = visibleLevelGuids.filter((levelGuid) =>
        insertions.some(
          ({ objectGuid, parentGuid }) => parentGuid === hierarchyFilter.activeBuildingGuid && objectGuid === levelGuid,
        ),
      );
    }

    if (hierarchyFilter.visibleLevels !== 'all') {
      visibleLevelGuids = visibleLevelGuids.filter(VisibilityConfig.getFilterFn(hierarchyFilter.visibleLevels));
    }

    return new Set(visibleLevelGuids);
  },
);

/**
 * Determine a single active level guid, suitable for use as the default level during insertion, etc. If no single level
 *   is clearly the sole active level, one will be chosen. Please don't depend on how that choice is made as it will
 *   likely change in the future.
 */
export const getActiveLayoutLevelGuidOrNull = createSelector(
  (state: RootState) => state.selections.layoutHierarchyFilter,
  getOstensiblyVisibleLevelGuids,

  (hierarchyFilter, visibleLevelGuids) => {
    // in "ghost objects" mode there is not actually an active level
    if (hierarchyFilter === 'ghost') {
      return null;
    }

    if (hierarchyFilter?.activeLevelGuid) {
      return hierarchyFilter.activeLevelGuid;
    }

    if (visibleLevelGuids.size > 0) {
      return pickSingleActiveLevelGuid(Array.from(visibleLevelGuids).filter(notNullish));
    }

    return null;
  },
);

export const getActiveLevelOrNull = createSelector(
  getAllLevelsDictionary,
  getActiveLayoutLevelGuidOrNull,
  (levels, activeLevelGuid) => (activeLevelGuid ? levels[activeLevelGuid] : null),
);

/** Used instead of `null` as a key in dictionaries keyed by level guid, since JavaScript can't hash null */
export const NULL_LEVEL_GUID_KEY = 'NULL_LEVEL_GUID_KEY';

/**
 * Returns the guids of all currently visible layers. The inclusion of `null` in the set signifies that items without a
 *   level guid should also be visible.
 */
export const getVisibleLevelGuids = createSimpleSetSelector(
  (state: RootState) => state.selections.isSelectionWindowToolEnabled,
  getActiveLayoutLevelGuidOrNull,
  getOstensiblyVisibleLevelGuids,

  (selectionWindowEnabled, activeLevelGuid, visibleLevels) => {
    const fallbackLevel = Array.from(visibleLevels)[0] ?? null;

    // when bulk selection is occuring limit to the active level as selection from multiple levels is forbidden
    if (selectionWindowEnabled) return new Set([activeLevelGuid ?? fallbackLevel]);

    return visibleLevels;
  },
);

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// standard selectors for fetching layout data; ensure that these stay consistent please
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getAllBearingEnvelopesDictionary = (state: RootState) => state.layout.bearingEnvelopes;
export const getAllBearingEnvelopes = createSelector(getAllBearingEnvelopesDictionary, (dict) => Object.values(dict));
export const getVisibleBearingEnvelopes = createSelector(
  [getAllBearingEnvelopes, getVisibleLevelGuids],
  filterVisibleItemsInLevels,
);
export const getVisibleBearingEnvelopesDictionary = createSelector(getVisibleBearingEnvelopes, toDictionary);
export const getVisibleBearingEnvelopeGuids = createSimpleSetSelector(getVisibleBearingEnvelopes, createGuidSet);
export const getVisibleBearingEnvelopeCount = createSelector(getVisibleBearingEnvelopeGuids, (guids) => guids.size);

////////////

export const getAllCeilingPlanesDictionary = (state: RootState) => state.layout.ceilingPlanes;
export const getAllCeilingPlanes = createSelector(getAllCeilingPlanesDictionary, (dict) => Object.values(dict));
export const getVisibleCeilingPlanes = createSelector(
  [getAllCeilingPlanes, getVisibleLevelGuids],
  filterVisibleItemsInLevels,
);
export const getVisibleCeilingPlanesDictionary = createSelector(getVisibleCeilingPlanes, toDictionary);
export const getVisibleCeilingPlaneGuids = createSimpleSetSelector(getVisibleCeilingPlanes, createGuidSet);
export const getVisibleCeilingPlaneCount = createSelector(getVisibleCeilingPlaneGuids, (guids) => guids.size);

////////////

export const getAllCeilingTraysDictionary = (state: RootState) => state.layout.ceilingTrays;
export const getAllCeilingTrays = createSelector(getAllCeilingTraysDictionary, (dict) => Object.values(dict));
export const getVisibleCeilingTrays = createSelector(
  [getAllCeilingTrays, getVisibleLevelGuids],
  filterVisibleItemsInLevels,
);
export const getVisibleCeilingTraysDictionary = createSelector(getVisibleCeilingTrays, toDictionary);
export const getVisibleCeilingTrayGuids = createSimpleSetSelector(getVisibleCeilingTrays, createGuidSet);
export const getVisibleCeilingTrayCount = createSelector(getVisibleCeilingTrayGuids, (guids) => guids.size);

////////////

export const getAllFeaturePatternContainersDictionary = (state: RootState) => state.layout.featurePatternContainers;
export const getAllFeaturePatternContainers = createSelector(getAllFeaturePatternContainersDictionary, (dict) =>
  Object.values(dict),
);
export const getVisibleFeaturePatternContainers = createSelector(
  [getAllFeaturePatternContainers, getVisibleLevelGuids],
  filterVisibleItemsInLevels,
);
export const getVisibleFeaturePatternContainersDictionary = createSelector(
  getVisibleFeaturePatternContainers,
  toDictionary,
);
export const getVisibleFeaturePatternContainerGuids = createSimpleSetSelector(
  getVisibleFeaturePatternContainers,
  createGuidSet,
);
export const getVisibleFeaturePatternContainerCount = createSelector(
  getVisibleFeaturePatternContainerGuids,
  (guids) => guids.size,
);

////////////

export const getAllRoofContainersDictionary = (state: RootState) => state.layout.roofContainers;
export const getAllRoofContainers = createSelector(getAllRoofContainersDictionary, (dict) => Object.values(dict));
export const getVisibleRoofContainers = createSelector(
  [getAllRoofContainers, getVisibleLevelGuids],
  filterVisibleItemsInLevels,
);
export const getVisibleRoofContainersDictionary = createSelector(getVisibleRoofContainers, toDictionary);
export const getVisibleRoofContainerGuids = createSimpleSetSelector(getVisibleRoofContainers, createGuidSet);
export const getVisibleRoofContainerCount = createSelector(getVisibleRoofContainerGuids, (guids) => guids.size);

////////////

export const getAllRoofPlanesDictionary = (state: RootState) => state.layout.roofPlanes;
export const getAllRoofPlanes = createSelector(getAllRoofPlanesDictionary, (dict) => Object.values(dict));
export const getVisibleRoofPlanes = createSelector(
  [getAllRoofPlanes, getVisibleLevelGuids],
  filterVisibleItemsInLevels,
);
export const getVisibleRoofPlanesDictionary = createSelector(getVisibleRoofPlanes, toDictionary);
export const getVisibleRoofPlaneGuids = createSimpleSetSelector(getVisibleRoofPlanes, createGuidSet);
export const getVisibleRoofPlaneCount = createSelector(getVisibleRoofPlaneGuids, (guids) => guids.size);

////////////

export const getAllTrussEnvelopesDictionary = (state: RootState) => state.layout.trussEnvelopes;
export const getAllTrussEnvelopes = createSelector(getAllTrussEnvelopesDictionary, (dict) => Object.values(dict));

export const getAllTrussEnvelopeNames = createSelector(getAllTrussEnvelopes, (trussEnvelopes) =>
  trussEnvelopes.map((envelope) => envelope.name),
);

export const getVisibleTrussEnvelopes = createSelector(
  [getAllTrussEnvelopes, getVisibleLevelGuids, getVisibleRoofContainerGuids],
  (trussEnvelopes, visibleLevels, visibleRoofContainerGuids) =>
    filterVisibleItemsInLevels(trussEnvelopes, visibleLevels).filter(
      (envelope) => envelope.roofContainerGuid == null || visibleRoofContainerGuids.has(envelope.roofContainerGuid),
    ),
);
export const getVisibleTrussEnvelopesDictionary = createSelector(getVisibleTrussEnvelopes, toDictionary);
export const getVisibleTrussEnvelopeGuids = createSimpleSetSelector(getVisibleTrussEnvelopes, createGuidSet);
export const getVisibleTrussEnvelopeCount = createSelector(getVisibleTrussEnvelopeGuids, (guids) => guids.size);

export const getVisibleDesignedEnvelopes = createSelector(getVisibleTrussEnvelopes, (trussEnvelopes) =>
  trussEnvelopes.filter((envelope) => envelope.componentDesignGuid !== null),
);

function getTrussEnvelopeGuidsByComponentDesignGuidFromEnvelopes(
  trussEnvelopes: readonly Pick<TrussEnvelope, 'componentDesignGuid' | 'guid'>[],
): Record<string, readonly string[]> {
  const mapping: Record<string, string[]> = {};
  for (const trussEnvelope of trussEnvelopes) {
    if (trussEnvelope.componentDesignGuid == null) continue;

    if (!mapping[trussEnvelope.componentDesignGuid]) {
      mapping[trussEnvelope.componentDesignGuid] = [];
    }
    mapping[trussEnvelope.componentDesignGuid].push(trussEnvelope.guid);
  }
  return mapping;
}

export const getTrussEnvelopeGuidsByComponentDesignGuid = createSelector(
  getAllTrussEnvelopes,
  getTrussEnvelopeGuidsByComponentDesignGuidFromEnvelopes,
);

export const getVisibleTrussEnvelopeGuidsByComponentDesignGuid = createSelector(
  getVisibleTrussEnvelopes,
  getTrussEnvelopeGuidsByComponentDesignGuidFromEnvelopes,
);

////////////

// snapping underlays are universal -- not level specific

export const getAllSnappingUnderlaysDictionary = (state: RootState) => state.layout.snappingUnderlays;
export const getAllSnappingUnderlays = createSelector(getAllSnappingUnderlaysDictionary, (dict) => Object.values(dict));

////////////

export const getAllWorkLinesDictionary = (state: RootState) => state.layout.workLines;
export const getAllWorkLines = createSelector(getAllWorkLinesDictionary, (dict) => Object.values(dict));
export const getVisibleWorkLines = createSelector(
  [getAllWorkLines, getLayoutHierarchyFilter, getVisibleLevelGuids],
  (workLines, hierarchyFilter, visibleLevelGuids) =>
    workLines.filter((workLine) => {
      switch (workLine.scope.type) {
        case 'Project':
          return true;

        case 'Building':
          return hierarchyFilter !== 'ghost' && hierarchyFilter.activeBuildingGuid === workLine.scope.buildingGuid;

        case 'Level':
          return hierarchyFilter !== 'ghost' && visibleLevelGuids.has(workLine.scope.levelGuid);

        default:
          assertExhaustiveSwitch(workLine.scope);
      }
    }),
);
export const getVisibleWorkLinesDictionary = createSelector(getVisibleWorkLines, toDictionary);
export const getVisibleWorkLineGuids = createSimpleSetSelector(getVisibleWorkLines, createGuidSet);
export const getVisibleWorkLineCount = createSelector(getVisibleWorkLineGuids, (guids) => guids.size);

export const getDefaultWorkLineScope = createSelector(
  getLayoutHierarchyFilter,
  getOstensiblyVisibleLevelGuids,

  (hierarchyFilter, visibleLevelGuids): WorkLineScope => {
    if (hierarchyFilter === 'ghost') return { type: 'Project' };

    const levelGuids = Array.from(visibleLevelGuids).filter(notNullish);
    if (levelGuids.length === 1) return { type: 'Level', levelGuid: levelGuids[0] };

    if (hierarchyFilter.activeBuildingGuid)
      return { type: 'Building', buildingGuid: hierarchyFilter.activeBuildingGuid };

    return { type: 'Project' };
  },
);

/** `null` when any affected work line is project-scoped, since the edit is then always partly visible. */
export function getLevelGuidsAffectedByWorkLineEdit(
  state: RootState,
  workLineGuids: readonly string[],
  newScopes: readonly WorkLineScope[],
): string[] | null {
  const existingScopes = workLineGuids.map((guid) => state.layout.workLines[guid]?.scope).filter(notNullish);
  const scopes = [...existingScopes, ...newScopes];
  if (scopes.some((scope) => scope.type === 'Project')) return null;

  const allLevelGuids = getAllLevelGuids(state);
  const insertions = getInsertionsWithPendingChanges(state);

  const levelGuids = uniq(
    scopes.flatMap((scope) => {
      switch (scope.type) {
        case 'Project':
          return [];

        case 'Building':
          return insertions
            .filter(
              (insertion) => insertion.parentGuid === scope.buildingGuid && allLevelGuids.has(insertion.objectGuid),
            )
            .map((insertion) => insertion.objectGuid);

        case 'Level':
          return [scope.levelGuid];

        default:
          assertExhaustiveSwitch(scope);
      }
    }),
  );

  return levelGuids.length > 0 ? levelGuids : null;
}

////////////

const getLayoutUserDistanceDimensions = (state: RootState) => state.layout.userDistanceDimensions;

export const getLocalLayoutUserDistanceDimensions = (state: RootState) => state.layout.localUserDistanceDimensions;
export const getLocalLayoutUserDistanceDimensionsCount = createSelector(
  getLocalLayoutUserDistanceDimensions,
  (dimensions) => dimensions.length,
);

export const getAllLayoutUserDistanceDimensions = createSelector(
  getLayoutUserDistanceDimensions,
  getLocalLayoutUserDistanceDimensions,
  (stored, local) => [...stored, ...local],
);
export const getAllLayoutUserDistanceDimensionsCount = createSelector(
  getAllLayoutUserDistanceDimensions,
  (dimensions) => dimensions.length,
);

////////////

export const getVisibleComponentDesignGuids = createSimpleSetSelector(
  getVisibleTrussEnvelopes,
  (visibleEnvelopes) => new Set(visibleEnvelopes.map((envelope) => envelope.componentDesignGuid).filter(notNullish)),
);
export const getVisibleComponentDesignCount = createSelector(getVisibleComponentDesignGuids, (guids) => guids.size);

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// "fundamental layout data" utilities -- for when you need all of the basic layout data in one place. Not recommended.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type FundamentalLayoutData = Pick<
  LayoutState,
  | 'bearingEnvelopes'
  | 'ceilingPlanes'
  | 'ceilingTrays'
  | 'featurePatternContainers'
  | 'roofContainers'
  | 'roofPlanes'
  | 'trussEnvelopes'
  | 'workLines'
>;

// Not recommended to use unless you know you'll be needed all of these. Be wary of cache implications.
export const getAllLayoutObjectsCategorized = createSelector(
  getAllBearingEnvelopesDictionary,
  getAllCeilingPlanesDictionary,
  getAllCeilingTraysDictionary,
  getAllFeaturePatternContainersDictionary,
  getAllRoofContainersDictionary,
  getAllRoofPlanesDictionary,
  getAllTrussEnvelopesDictionary,
  getAllWorkLinesDictionary,

  (
    bearingEnvelopes,
    ceilingPlanes,
    ceilingTrays,
    featurePatternContainers,
    roofContainers,
    roofPlanes,
    trussEnvelopes,
    workLines,
  ): FundamentalLayoutData => ({
    bearingEnvelopes,
    ceilingPlanes,
    ceilingTrays,
    featurePatternContainers,
    roofContainers,
    roofPlanes,
    trussEnvelopes,
    workLines,
  }),
);

// Not recommended to use unless you know you'll be needed all of these. Be wary of cache implications.
export const getVisibleLayoutObjectsCategorized = createSelector(
  getVisibleBearingEnvelopesDictionary,
  getVisibleCeilingPlanesDictionary,
  getVisibleCeilingTraysDictionary,
  getVisibleFeaturePatternContainersDictionary,
  getVisibleRoofContainersDictionary,
  getVisibleRoofPlanesDictionary,
  getVisibleTrussEnvelopesDictionary,
  getVisibleWorkLinesDictionary,

  (
    bearingEnvelopes,
    ceilingPlanes,
    ceilingTrays,
    featurePatternContainers,
    roofContainers,
    roofPlanes,
    trussEnvelopes,
    workLines,
  ): FundamentalLayoutData => ({
    bearingEnvelopes,
    ceilingPlanes,
    ceilingTrays,
    featurePatternContainers,
    roofContainers,
    roofPlanes,
    trussEnvelopes,
    workLines,
  }),
);

export const getAllLayoutObjects = createSelector(getAllLayoutObjectsCategorized, (data) =>
  Object.values(data).flatMap(Object.values),
);

export const getVisibleLayoutObjects = createSelector(getVisibleLayoutObjectsCategorized, (data) =>
  Object.values(data).flatMap(Object.values),
);

export const doGhostObjectsExistInCurrentLayout = createSelector(getAllLayoutObjects, (layoutObjects) => {
  return layoutObjects.some((object) => object.levelGuid === null);
});

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getLinkedComponentDesignGuids = createSimpleSetSelector(
  getAllTrussEnvelopes,
  (trussEnvelopes) => new Set(trussEnvelopes.map((envelope) => envelope.componentDesignGuid)),
);

export type LayoutModelOccupancyReport = {
  /**
   * Records whether a layout model contains any layout objects. For a building, this means whether any levels belonging
   *   to it are occupied.
   *
   * Note that this means that any number of empty level(s) can belong to a building without it being considered occupied.
   *   This definition was chosen to fit the needs of the deletion layer which needs to determine whether a building is
   *   "empty enough" to be deleted.
   */
  occupiedModels: Map<string, boolean>;

  /**
   * Isolated levels are the only level in their respective building (thus not safe to delete).
   */
  isolatedLevels: Set<string>;
};

export const getLayoutModelOccupancyReport = createSelector(
  getLayoutModels,
  getInsertionsWithPendingChanges,

  getAllLayoutObjects,
  getAllWorkLines,

  (layoutModels, insertions, layoutObjects, workLines): LayoutModelOccupancyReport => {
    const occupiedModels = new Map<string, boolean>();
    const isolatedLevels = new Set<string>();

    const levels = layoutModels.filter((model) => model.type === 'level');
    const buildings = layoutModels.filter((model) => model.type === 'building');

    const levelGuids = new Set(levels.map((level) => level.guid));
    const occupiedLevelGuids = new Set(layoutObjects.map((object) => object.levelGuid).filter(notNullish));
    const occupiedBuildingGuids = new Set<string>();

    for (const { scope } of workLines) {
      if (scope.type === 'Level') occupiedLevelGuids.add(scope.levelGuid);
      if (scope.type === 'Building') occupiedBuildingGuids.add(scope.buildingGuid);
    }

    for (const level of levels) {
      occupiedModels.set(level.guid, occupiedLevelGuids.has(level.guid));
    }

    for (const building of buildings) {
      const insertionsInBuilding = insertions.filter((insertion) => insertion.parentGuid === building.guid);

      const insertedLevelGuids = insertionsInBuilding
        .filter((insertion) => levelGuids.has(insertion.objectGuid))
        .map((insertion) => insertion.objectGuid);

      const someContainedLevelsAreOccupied = insertedLevelGuids.some((levelGuid) => occupiedModels.get(levelGuid));
      const unrecognizedInsertions = insertionsInBuilding.filter((insertion) => !levelGuids.has(insertion.objectGuid));

      occupiedModels.set(
        building.guid,
        someContainedLevelsAreOccupied || unrecognizedInsertions.length > 0 || occupiedBuildingGuids.has(building.guid),
      );

      if (insertedLevelGuids.length === 1) {
        isolatedLevels.add(insertedLevelGuids[0]);
      }
    }

    return { occupiedModels, isolatedLevels };
  },
);

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// selectors and utilities for deriving default elevations for objects in a layout hierarchy context
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type HierarchyBearingEnvelopeElevations = Partial<Pick<BearingEnvelope, 'top' | 'bottom'>>;

export function getBearingEnvelopeHeight(envelope: Partial<Pick<BearingEnvelope, 'top' | 'bottom'>>) {
  return envelope.top != null && envelope.bottom != null ? envelope.top - envelope.bottom : null;
}

export function getBearingEnvelopeElevationsFromLevel(
  level: Pick<Level, 'plateElevation' | 'wallHeight'>,
  envelope: Pick<BearingEnvelope, 'bearingType' | 'beamOrientation'> & Partial<Pick<BearingEnvelope, 'top' | 'bottom'>>,
): HierarchyBearingEnvelopeElevations {
  switch (envelope.bearingType) {
    case 'Beam': {
      const beamHeight = getBearingEnvelopeHeight(envelope);
      if (beamHeight == null) return {};

      switch (envelope.beamOrientation) {
        case 'Drop':
          return {
            top: level.plateElevation,
            bottom: level.plateElevation - beamHeight,
          };

        case 'Flush':
          return {
            top: level.plateElevation + beamHeight,
            bottom: level.plateElevation,
          };
      }
    }

    case 'Wall': {
      return {
        top: level.plateElevation,
        bottom: level.plateElevation - level.wallHeight,
      };
    }

    default:
      assertExhaustiveSwitch(envelope.bearingType);
  }
}

export function bearingEnvelopeElevationsMatch(
  first: HierarchyBearingEnvelopeElevations,
  second: HierarchyBearingEnvelopeElevations,
) {
  return first.top === second.top && first.bottom === second.bottom;
}

export const getBearingEnvelopeElevationsFromActiveLevel = createSelector(
  getActiveLevelOrNull,
  (activeLevel): HierarchyBearingEnvelopeElevations => {
    if (activeLevel == null) return {};

    return {
      top: activeLevel.plateElevation,
      bottom: activeLevel.plateElevation - activeLevel.wallHeight,
    };
  },
);

/**
 * The elevations for a feature pattern container that can be derived from the active level.
 */
type HierarchyFeaturePatternContainerElevations = Partial<Pick<FloorPolyline3D, 'topElevation' | 'bottomElevation'>>;

export function featurePatternContainerElevationsMatch(
  // note: intended for use with a `FeaturePatternTrussContainer`
  container: { shape: Pick<FloorPolyline3D, 'topElevation' | 'bottomElevation'> },
  elevations: HierarchyFeaturePatternContainerElevations,
) {
  return (
    container.shape.topElevation === elevations.topElevation &&
    container.shape.bottomElevation === elevations.bottomElevation
  );
}

export const getFeaturePatternContainerElevationsFromActiveLevel = createSelector(
  getActiveLevelOrNull,
  (activeLevel): HierarchyFeaturePatternContainerElevations => {
    if (activeLevel == null) return {};

    return {
      bottomElevation: activeLevel.plateElevation,
      topElevation: activeLevel.plateElevation + activeLevel.floorHeight,
    };
  },
);

/**
 * The elevation of a ceiling tray that can be derived from the active level. Note that ceiling trays have a
 *    `topElevation` as well, which is not automatically derived.
 */
type HierarchyCeilingTrayElevations = Partial<Pick<CeilingTray, 'bottomElevation'>>;

export function ceilingTrayElevationMatches(
  first: HierarchyCeilingTrayElevations,
  second: HierarchyCeilingTrayElevations,
) {
  return first.bottomElevation === second.bottomElevation;
}

export const getCeilingTrayElevationFromActiveLevel = createSelector(
  getActiveLevelOrNull,
  (activeLevel): HierarchyCeilingTrayElevations => (activeLevel ? { bottomElevation: activeLevel.plateElevation } : {}),
);

export const getAbsolutePlaneElevationFromActiveLevel = createSelector(
  getActiveLevelOrNull,
  (activeLevel): number | null => (activeLevel ? activeLevel.plateElevation : null),
);

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// selectors for determining what level layout object(s) belong to. Would be straightforward, but feature areas exist.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// these types are not exported; I really don't want to be using them in other places

const HierarchyObjectTypes = [
  'beam',
  'ceilingTray',
  'ceilingPlane',
  'featureArea',
  'featurePatternContainer',
  'roofContainer',
  'roofPlane',
  'trussEnvelope',
  'wall',
] as const;

type HierarchyObjectType = (typeof HierarchyObjectTypes)[number];

type HierarchyObject = SelectableItemOfType<HierarchyObjectType>;

export function hasLevelGuid(
  entry: SelectableItemEntry,
): entry is SelectableItemEntry & SelectableEntryOfType<HierarchyObjectType> {
  return (HierarchyObjectTypes as readonly string[]).includes(entry.type);
}

export const getFeatureAreaLevelGuids = createSelector(getAllFeaturePatternContainers, (featurePatternContainers) => {
  const result = new Map<string, string>();

  for (const container of featurePatternContainers) {
    if (container.levelGuid == null) continue;

    for (const featureArea of container.featureAreas) {
      result.set(featureArea.guid, container.levelGuid);
    }
  }

  return result;
});

export const createGetLevelGuid = createSelector(getFeatureAreaLevelGuids, (featureAreaLevelMapping) => {
  return function getLevelGuid(obj: HierarchyObject): string | null {
    return 'levelGuid' in obj ? obj.levelGuid : (featureAreaLevelMapping.get(obj.guid) ?? null);
  };
});

export const createGetLevelGuids = createSelector(createGetLevelGuid, (getLevelGuid) => {
  return function getLevelGuids(objs: HierarchyObject[]): string[] {
    return uniq(objs.map(getLevelGuid).filter(notNullish));
  };
});
